// Lê a localização gravada no corpo da mensagem.
//
// O formato canônico é o que o backend monta em `helpers/Localizacao.ts`:
//
//   <miniatura ou vazio>|<link do mapa>|<descrição>
//
// Mas a conversa tem histórico: mensagens antigas foram gravadas como
// "link|descrição" (duas partes — e era isto que quebrava a tela: o link ia
// para o <img> e a descrição para o href, então "Visualizar" tentava abrir
// "Rua tal" como se fosse endereço de página e o app saía do atendimento), e
// as do Telegram vinham sem "|" nenhum. Aqui tudo isso é aceito.
//
// Devolve null quando não dá para dizer onde é — sem link nem coordenadas não
// há mapa para desenhar, e a mensagem segue como texto comum.

const URL_HTTP = /^https?:\/\//i;

const primeiroLink = (texto = "") => {
	const achado = texto.match(/https?:\/\/[^\s|]+/i);
	return achado ? achado[0] : "";
};

// As coordenadas estão no link, em um dos formatos que o Google e o
// OpenStreetMap usam: ?query=, ?q=, ?mlat=/?mlon= ou @lat,lon.
export const coordenadasDoLink = (link = "") => {
	const alvo = decodeURIComponent(link);

	const porParametro = alvo.match(
		/[?&](?:query|q|ll|destination|center)=(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/i,
	);
	if (porParametro) {
		return {
			latitude: Number(porParametro[1]),
			longitude: Number(porParametro[2]),
		};
	}

	const porArroba = alvo.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
	if (porArroba) {
		return {
			latitude: Number(porArroba[1]),
			longitude: Number(porArroba[2]),
		};
	}

	const mlat = alvo.match(/[?&]mlat=(-?\d+(?:\.\d+)?)/i);
	const mlon = alvo.match(/[?&]mlon=(-?\d+(?:\.\d+)?)/i);
	if (mlat && mlon) {
		return { latitude: Number(mlat[1]), longitude: Number(mlon[1]) };
	}

	return null;
};

const coordenadasSoltas = (texto = "") => {
	const achado = texto.match(/(-?\d{1,3}\.\d{3,})\s*,\s*(-?\d{1,3}\.\d{3,})/);

	return achado
		? { latitude: Number(achado[1]), longitude: Number(achado[2]) }
		: null;
};

export const lerLocalizacao = (corpo = "") => {
	if (!corpo) return null;

	const partes = String(corpo).split("|");
	const indiceDoLink = partes.findIndex((parte) => URL_HTTP.test(parte.trim()));

	let imagem = "";
	let link = "";
	let descricao = "";

	if (indiceDoLink >= 0) {
		link = partes[indiceDoLink].trim();
		descricao = partes
			.slice(indiceDoLink + 1)
			.join("|")
			.trim();

		const anterior = (partes[indiceDoLink - 1] || "").trim();
		// Só é miniatura o que o navegador sabe desenhar: o formato antigo
		// punha o próprio link aqui, e era isso que virava imagem quebrada.
		if (/^data:image\//i.test(anterior)) imagem = anterior;
	} else {
		link = primeiroLink(corpo);
		descricao = String(corpo).replace(link, "").split("|").join(" ").trim();
	}

	const coordenadas =
		coordenadasDoLink(link) || coordenadasSoltas(corpo) || null;

	if (!link && !coordenadas) return null;

	// Sem link mas com coordenadas (mensagem antiga, ou canal que só manda os
	// números): o mapa é montado a partir delas.
	if (!link && coordenadas) {
		link = `https://www.google.com/maps/search/?api=1&query=${coordenadas.latitude},${coordenadas.longitude}`;
	}

	// "Localization" era o rótulo fixo que o sistema gravava quando não sabia o
	// nome do lugar; não é descrição de nada.
	if (/^localization$/i.test(descricao)) descricao = "";

	return {
		imagem,
		link,
		descricao,
		latitude: coordenadas?.latitude ?? null,
		longitude: coordenadas?.longitude ?? null,
	};
};

// Endereço do mapa embutido do OpenStreetMap — usado quando a mensagem não
// trouxe miniatura. Não precisa de chave de API, ao contrário do Google.
export const mapaEmbutido = (latitude, longitude) => {
	const margem = 0.003;
	const bbox = [
		longitude - margem,
		latitude - margem,
		longitude + margem,
		latitude + margem,
	].join("%2C");

	return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude}%2C${longitude}`;
};

export const linkDoGoogleMaps = (latitude, longitude) =>
	`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

// Miniatura do mapa montada com os quadrados (tiles) do OpenStreetMap.
//
// O mapa embutido (`mapaEmbutido`) é interativo e traz botões de zoom e a
// faixa de "Reportar um problema | © Contribuidores do OpenStreetMap…" por
// cima — dentro de um cartão de 130 px de altura isso vira poluição e parece
// tela quebrada. Aqui são só as imagens: nove quadrados de 256 px em volta do
// ponto, deslocados para o alfinete cair no centro.
//
// A atribuição continua obrigatória pela licença do OpenStreetMap — vai como
// um selo discreto no canto do cartão.
export const quadradosDoMapa = (
	latitude,
	longitude,
	{ largura = 260, altura = 130, zoom = 16 } = {},
) => {
	const n = 2 ** zoom;
	const radianos = (latitude * Math.PI) / 180;

	const x = ((longitude + 180) / 360) * n;
	const y =
		((1 -
			Math.log(Math.tan(radianos) + 1 / Math.cos(radianos)) / Math.PI) /
			2) *
		n;

	const primeiroX = Math.floor(x) - 1;
	const primeiroY = Math.floor(y) - 1;

	// Onde o ponto cai dentro do bloco de 3x3, em pixels.
	const pontoX = (x - primeiroX) * 256;
	const pontoY = (y - primeiroY) * 256;

	const deslocamentoX = largura / 2 - pontoX;
	const deslocamentoY = altura / 2 - pontoY;

	const quadrados = [];

	for (let linha = 0; linha < 3; linha += 1) {
		for (let coluna = 0; coluna < 3; coluna += 1) {
			const tx = primeiroX + coluna;
			const ty = primeiroY + linha;

			// Fora do mundo: nos polos e além da borda o servidor devolve 404.
			if (ty < 0 || ty >= n) continue;

			const tileX = ((tx % n) + n) % n;

			quadrados.push({
				chave: `${zoom}-${tileX}-${ty}`,
				url: `https://tile.openstreetmap.org/${zoom}/${tileX}/${ty}.png`,
				left: deslocamentoX + coluna * 256,
				top: deslocamentoY + linha * 256,
			});
		}
	}

	return quadrados;
};
